import {
  createContext,
  ReactNode,
  useState,
  useCallback,
  useEffect,
  useContext,
  useMemo,
  useRef,
} from 'react'
import {
  userAPI,
  type UserEnrollmentInput,
  type UserMembership,
  type UserProfile,
} from '@/services/api/user'
import {
  getAuthMode,
  getFixedOrganizationId,
  isConsoleScopedInstance,
  type AuthMode,
} from '@/config/runtime'
import {
  localAuthAPI,
  type LocalAuthUserOption,
  type LocalLoginResponse,
} from '@/services/localAuth'
import {
  autoPairConsoleBinding,
  getConsoleBindingState,
  hasStaleConsoleBinding,
} from '@/services/consoleBinding'
import {
  getAuthToken,
  getCurrentAuthUser,
  getPrimaryEmail,
  onSessionCreated,
  onSessionExpired,
  registerHankoElements,
  signOut as hankoSignOut,
  type HankoUser,
} from '@/services/hankoAuth'
import { isTauriRuntimeAvailable } from '@/integrations/tauri/runtimeConfig'
import { useContextStore } from '@/store/context'
import { useCalistoStore } from '@/store/calisto'
import { selectScopedResources } from './scopedOrganizations'

const CONTEXT_STORAGE_KEYS = [
  'agent-os.current-org-id',
  'agent-os.current-deployment-id',
  'agent-os.current-workspace-id',
]
const LOCAL_AUTH_INVALIDATED_EVENT = 'agent-maurice:local-auth-invalidated'
const CONSOLE_AUTO_PAIR_FAILURE_KEY = 'agent-os.console.auto-pair.last-failure'
const CONSOLE_AUTO_PAIR_COOLDOWN_MS = 5 * 60 * 1000
const AUTH_REQUEST_CACHE_TTL_MS = 1500

let provisionPromise: Promise<unknown> | null = null
const bootstrapRequests = new Map<string, {
  expiresAt: number
  promise: ReturnType<typeof userAPI.bootstrap>
}>()
const currentUserRequests = new Map<string, {
  expiresAt: number
  promise: ReturnType<typeof userAPI.getCurrentUser>
}>()

function getAuthRequestKey(token?: string | null) {
  return token || localStorage.getItem('auth_token') || 'anonymous'
}

function pruneAuthRequestCache() {
  const now = Date.now()
  for (const [key, entry] of bootstrapRequests) {
    if (entry.expiresAt <= now) {
      bootstrapRequests.delete(key)
    }
  }
  for (const [key, entry] of currentUserRequests) {
    if (entry.expiresAt <= now) {
      currentUserRequests.delete(key)
    }
  }
}

function provisionOnce() {
  if (!provisionPromise) {
    provisionPromise = userAPI.provision().finally(() => {
      provisionPromise = null
    })
  }
  return provisionPromise
}

function bootstrapOnce(token?: string | null) {
  pruneAuthRequestCache()
  const key = getAuthRequestKey(token)
  const existing = bootstrapRequests.get(key)
  if (existing) {
    return existing.promise
  }

  const promise = userAPI.bootstrap().finally(() => {
    globalThis.setTimeout(() => {
      const cached = bootstrapRequests.get(key)
      if (cached?.promise === promise && cached.expiresAt <= Date.now()) {
        bootstrapRequests.delete(key)
      }
    }, AUTH_REQUEST_CACHE_TTL_MS)
  })
  bootstrapRequests.set(key, {
    expiresAt: Date.now() + AUTH_REQUEST_CACHE_TTL_MS,
    promise,
  })
  return promise
}

function getCurrentUserOnce(token?: string | null) {
  pruneAuthRequestCache()
  const key = getAuthRequestKey(token)
  const existing = currentUserRequests.get(key)
  if (existing) {
    return existing.promise
  }

  const promise = userAPI.getCurrentUser().finally(() => {
    globalThis.setTimeout(() => {
      const cached = currentUserRequests.get(key)
      if (cached?.promise === promise && cached.expiresAt <= Date.now()) {
        currentUserRequests.delete(key)
      }
    }, AUTH_REQUEST_CACHE_TTL_MS)
  })
  currentUserRequests.set(key, {
    expiresAt: Date.now() + AUTH_REQUEST_CACHE_TTL_MS,
    promise,
  })
  return promise
}

function isEnrollmentConflict(error: unknown) {
  return (
    error instanceof Error &&
    (error.message.includes('API Error 409') ||
      error.message.toLowerCase().includes('already enrolled'))
  )
}

function filterScopedMemberships(memberships: UserMembership[]): UserMembership[] {
  return selectScopedResources(
    memberships,
    (membership) => membership.organization.id,
    isConsoleScopedInstance(),
    getFixedOrganizationId(),
  )
}

interface AuthenticatedUser {
  id: string
  email: string
  displayName?: string | null
  photoUrl?: string | null
  username?: string | null
  firstName?: string | null
  lastName?: string | null
  language?: string | null
}

function readMetadataString(
  metadata: Record<string, unknown>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = metadata[key]
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }

  return undefined
}

function normalizeLanguage(value?: string | null): string | null {
  const normalized = value?.trim().toLowerCase()
  if (!normalized) {
    return null
  }
  if (normalized.startsWith('fr')) return 'fr'
  if (normalized.startsWith('es')) return 'es'
  if (normalized.startsWith('en')) return 'en'
  return normalized
}

function deriveNamesFromDisplayName(displayName?: string | null) {
  const fullName = displayName?.trim()
  if (!fullName || fullName.includes('@')) {
    return { firstName: undefined, lastName: undefined }
  }

  const parts = fullName.split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return { firstName: undefined, lastName: undefined }
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: undefined }
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  }
}

function deriveUsername(email?: string | null, displayName?: string | null): string | null {
  const safeDisplayName = displayName?.trim()
  if (safeDisplayName && !safeDisplayName.includes('@')) {
    return safeDisplayName
      .normalize('NFKD')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '_')
      .toLowerCase() || null
  }

  const localPart = email?.split('@')[0]?.split('+')[0]?.trim()
  return localPart || null
}

function buildHankoFallbackUser(user: HankoUser): AuthenticatedUser {
  const metadata = user && typeof user === 'object'
    ? (user as Record<string, unknown>)
    : {}
  const email = getPrimaryEmail(user)
  const displayName =
    readMetadataString(metadata, ['full_name', 'name', 'display_name', 'username']) ||
    email ||
    null
  const metadataFirstName = readMetadataString(metadata, [
    'first_name',
    'given_name',
    'firstName',
  ])
  const metadataLastName = readMetadataString(metadata, [
    'last_name',
    'family_name',
    'lastName',
  ])
  const derivedNames = deriveNamesFromDisplayName(displayName)

  return {
    id: user.user_id || user.id || '',
    email,
    displayName,
    photoUrl: readMetadataString(metadata, ['avatar_url', 'picture']) || user.picture || null,
    username:
      readMetadataString(metadata, ['preferred_username', 'username', 'user_name']) ||
      deriveUsername(email, displayName),
    firstName: metadataFirstName || derivedNames.firstName || null,
    lastName: metadataLastName || derivedNames.lastName || null,
    language: normalizeLanguage(
      readMetadataString(metadata, ['preferred_language', 'language', 'locale']),
    ),
  }
}

function getStoredAuthUserFallback() {
  const id = localStorage.getItem('auth_user_id')
  const email = localStorage.getItem('auth_email')

  if (!id && !email) {
    return undefined
  }

  return {
    id: id || '',
    email: email || '',
    displayName: email || id || '',
    photoUrl: null,
  }
}

function isCompletedLocalProfile(profile: UserProfile | null | undefined): boolean {
  if (!profile) {
    return false
  }

  const username = profile.username?.trim().toLowerCase()
  if (!username || username === 'user' || username === 'unknown') {
    return false
  }

  return Boolean(
    profile.firstName?.trim() &&
      profile.lastName?.trim() &&
      profile.language?.trim(),
  )
}

function shouldAttemptAutoConsolePairing(authMode: AuthMode): boolean {
  if (authMode !== 'hanko' || isConsoleScopedInstance()) {
    return false
  }
  if (typeof window === 'undefined') {
    return false
  }

  const hostname = window.location.hostname.trim().toLowerCase()
  return hostname === 'localhost' || hostname === '127.0.0.1'
}

function buildAutoPairDeviceName(user: AuthenticatedUser | null | undefined): string {
  if (typeof window === 'undefined') {
    return 'AgentMaurice local'
  }

  const hostname = window.location.hostname.trim() || 'localhost'
  return user?.displayName?.trim()
    ? `AgentMaurice local — ${user.displayName.trim()} @ ${hostname}`
    : `AgentMaurice local — ${hostname}`
}

function shouldSkipConsoleAutoPairAfterFailure(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  const raw = window.localStorage.getItem(CONSOLE_AUTO_PAIR_FAILURE_KEY)
  const lastFailureAt = raw ? Number(raw) : 0
  return Number.isFinite(lastFailureAt) &&
    Date.now() - lastFailureAt < CONSOLE_AUTO_PAIR_COOLDOWN_MS
}

function markConsoleAutoPairFailure() {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(CONSOLE_AUTO_PAIR_FAILURE_KEY, String(Date.now()))
  }
}

function clearConsoleAutoPairFailure() {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(CONSOLE_AUTO_PAIR_FAILURE_KEY)
  }
}

interface AuthContextType {
  authMode: AuthMode
  isAuthenticated: boolean
  loading: boolean
  authError: string | null
  user: AuthenticatedUser | null
  userProfile: UserProfile | null
  organizations: UserMembership[]
  needsEnrollment: boolean
  requiresOrganization: boolean
  hasOrganization: boolean
  localUsers: LocalAuthUserOption[]
  defaultLocalUser: string | null
  idToken: string | null
  login: (email?: string, password?: string) => Promise<void>
  register: () => Promise<void>
  logout: () => Promise<void>
  refreshIdToken: () => Promise<string | null>
  refreshProfile: () => Promise<void>
  completeEnrollment: (input: UserEnrollmentInput) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const authMode = useMemo(() => getAuthMode(), [])
  const [loading, setLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const [idToken, setIdToken] = useState<string | null>(null)
  const [user, setUser] = useState<AuthContextType['user']>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [organizations, setOrganizations] = useState<UserMembership[]>([])
  const [needsEnrollment, setNeedsEnrollment] = useState(false)
  const [requiresOrganization, setRequiresOrganization] = useState(false)
  const [hasOrganization, setHasOrganization] = useState(false)
  const [localUsers, setLocalUsers] = useState<LocalAuthUserOption[]>([])
  const [defaultLocalUser, setDefaultLocalUser] = useState<string | null>(null)
  const currentUserIdRef = useRef<string | null>(null)
  const hydratedTokenRef = useRef<string | null>(null)

  useEffect(() => {
    currentUserIdRef.current = user?.id ?? null
  }, [user?.id])

  const resetScopedClientState = useCallback((userId?: string | null) => {
    for (const key of CONTEXT_STORAGE_KEYS) {
      localStorage.removeItem(key)
    }

    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index)
      if (!key) continue
      if (key.startsWith('agent-os.calisto.session.')) {
        localStorage.removeItem(key)
      }
    }

    if (userId) {
      localStorage.removeItem(`agent-os.calisto.first-build.${userId}`)
    }

    useContextStore.getState().resetContext()
    useCalistoStore.setState({
      currentSessionId: null,
      roomId: null,
      messages: [],
      sessionActive: false,
      chatOpen: false,
      isProcessing: false,
      lastResponse: null,
      error: null,
      canvasActions: [],
      observeTarget: null,
      contextSummary: null,
      guidedWorkflow: [],
      guidedConvergeLoading: false,
      guidedConvergePreparing: false,
      guidedConvergeApplying: false,
      guidedConvergeError: null,
      guidedConvergeStatusMessage: null,
    })
  }, [])

  const clearAuthState = useCallback(() => {
    const currentUserId = currentUserIdRef.current
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user_id')
    localStorage.removeItem('auth_email')
    hydratedTokenRef.current = null
    currentUserIdRef.current = null
    resetScopedClientState(currentUserId)
    setIdToken(null)
    setUser(null)
    setUserProfile(null)
    setOrganizations([])
    setNeedsEnrollment(false)
    setRequiresOrganization(false)
    setHasOrganization(false)
  }, [resetScopedClientState])

  const attemptAutoConsolePairing = useCallback(
    async (currentUser: AuthenticatedUser | null) => {
      if (!shouldAttemptAutoConsolePairing(authMode)) {
        return
      }

      try {
        const bindingState = await getConsoleBindingState()
        if (!bindingState.supported) {
          return
        }

        if (bindingState.binding && !hasStaleConsoleBinding(bindingState)) {
          return
        }

        if (shouldSkipConsoleAutoPairAfterFailure()) {
          return
        }

        await autoPairConsoleBinding({
          deviceName: buildAutoPairDeviceName(currentUser),
        })
        clearConsoleAutoPairFailure()

        resetScopedClientState(currentUser?.id)

        const bootstrap = await bootstrapOnce()
        setRequiresOrganization(Boolean(bootstrap?.requires_organization))
        setHasOrganization(Boolean(bootstrap?.has_organization))

        const refreshedUser = await getCurrentUserOnce()
        const scopedOrganizations = filterScopedMemberships(refreshedUser.organizations)
        const resolvedUser = refreshedUser.user
          ? {
              id: refreshedUser.user.id,
              email: refreshedUser.user.email,
              displayName:
                refreshedUser.user.displayName || refreshedUser.user.email,
              photoUrl: refreshedUser.user.photoUrl || null,
              username: refreshedUser.user.username || null,
              firstName: refreshedUser.user.firstName || null,
              lastName: refreshedUser.user.lastName || null,
              language: refreshedUser.user.language || null,
            }
          : currentUser

        setUser(resolvedUser)
        setUserProfile(refreshedUser.user)
        setOrganizations(scopedOrganizations)
        setHasOrganization(scopedOrganizations.length > 0)
        setNeedsEnrollment(false)
        setAuthError(null)
      } catch (error) {
        markConsoleAutoPairFailure()
        console.warn('Automatic console pairing failed:', error)
      }
    },
    [authMode, resetScopedClientState],
  )

  const hydrateAuthenticatedUser = useCallback(
    async (
      token: string,
      fallbackUser?: AuthenticatedUser,
    ) => {
      const resolvedFallbackUser = fallbackUser || getStoredAuthUserFallback()

      localStorage.setItem('auth_token', token)
      if (resolvedFallbackUser?.id) {
        localStorage.setItem('auth_user_id', resolvedFallbackUser.id)
      }
      if (resolvedFallbackUser?.email) {
        localStorage.setItem('auth_email', resolvedFallbackUser.email)
      }

      setIdToken(token)

      if (hydratedTokenRef.current === token && currentUserIdRef.current) {
        return
      }

      try {
        await provisionOnce()
      } catch (error) {
        console.warn('User provision failed:', error)
      }

      const bootstrap = await bootstrapOnce(token)
      const resolvedRequiresOrganization = Boolean(bootstrap?.requires_organization)
      const resolvedHasOrganization = Boolean(bootstrap?.has_organization)
      setRequiresOrganization(resolvedRequiresOrganization)
      setHasOrganization(resolvedHasOrganization)
      if (!bootstrap?.is_enrolled) {
        try {
          const currentUser = await getCurrentUserOnce(token)
          const scopedOrganizations = filterScopedMemberships(currentUser.organizations)
          const canRecoverEnrollmentState =
            isCompletedLocalProfile(currentUser.user) &&
            (!resolvedRequiresOrganization || scopedOrganizations.length > 0)

          if (canRecoverEnrollmentState) {
            const resolvedUser = currentUser.user
              ? {
                  id: currentUser.user.id,
                  email: currentUser.user.email,
                  displayName:
                    currentUser.user.displayName || currentUser.user.email,
                  photoUrl: currentUser.user.photoUrl || null,
                  username: currentUser.user.username || null,
                  firstName: currentUser.user.firstName || null,
                  lastName: currentUser.user.lastName || null,
                  language: currentUser.user.language || null,
                }
              : resolvedFallbackUser || null

            setUser(resolvedUser)
            currentUserIdRef.current = resolvedUser?.id ?? null
            hydratedTokenRef.current = token
            setUserProfile(currentUser.user)
            setOrganizations(scopedOrganizations)
            setHasOrganization(scopedOrganizations.length > 0)
            setNeedsEnrollment(false)
            setAuthError(null)
            return
          }
        } catch (error) {
          console.warn('Failed to recover enrollment state from /user/me:', error)
        }

        setUser(resolvedFallbackUser || null)
        currentUserIdRef.current = resolvedFallbackUser?.id ?? null
        hydratedTokenRef.current = token
        setUserProfile(null)
        setOrganizations([])
        setNeedsEnrollment(true)
        setAuthError(null)
        void attemptAutoConsolePairing(resolvedFallbackUser || null)
        return
      }

      try {
        const currentUser = await getCurrentUserOnce(token)
        const resolvedUser = currentUser.user
          ? {
              id: currentUser.user.id,
              email: currentUser.user.email,
              displayName:
                currentUser.user.displayName || currentUser.user.email,
              photoUrl: currentUser.user.photoUrl || null,
              username: currentUser.user.username || null,
              firstName: currentUser.user.firstName || null,
              lastName: currentUser.user.lastName || null,
              language: currentUser.user.language || null,
            }
          : resolvedFallbackUser || null

        setUser(resolvedUser)
        currentUserIdRef.current = resolvedUser?.id ?? null
        hydratedTokenRef.current = token
        setUserProfile(currentUser.user)
        const scopedOrganizations = filterScopedMemberships(currentUser.organizations)
        setOrganizations(scopedOrganizations)
        setHasOrganization(scopedOrganizations.length > 0)
        setNeedsEnrollment(false)
        setAuthError(null)
        void attemptAutoConsolePairing(resolvedUser)
      } catch (error) {
        console.error('Failed to load current user:', error)
        clearAuthState()
        throw error
      }
    },
    [attemptAutoConsolePairing, clearAuthState],
  )

  const applyLocalLogin = useCallback(
    async (response: LocalLoginResponse) => {
      await hydrateAuthenticatedUser(response.session_token, {
        id: response.user.uid,
        email: response.user.email,
        displayName: response.user.name || response.user.email,
        photoUrl: null,
      })
    },
    [hydrateAuthenticatedUser],
  )

  useEffect(() => {
    if (authMode !== 'local' || typeof window === 'undefined') {
      return
    }

    const handleLocalAuthInvalidated = () => {
      clearAuthState()
      setAuthError('Local session is no longer valid. Please sign in again.')
      setLoading(false)
    }

    window.addEventListener(LOCAL_AUTH_INVALIDATED_EVENT, handleLocalAuthInvalidated)
    return () => {
      window.removeEventListener(LOCAL_AUTH_INVALIDATED_EVENT, handleLocalAuthInvalidated)
    }
  }, [authMode, clearAuthState])

  useEffect(() => {
    let active = true

    const init = async () => {
      try {
        setAuthError(null)

        if (authMode === 'local') {
          const config = await localAuthAPI.listUsers()
          if (!active) return

          const sortedUsers = [...(config.users || [])].sort((left, right) =>
            left.email.localeCompare(right.email),
          )
          setLocalUsers(sortedUsers)
          setDefaultLocalUser(config.default_user || null)

          const existingToken = localStorage.getItem('auth_token')
          if (existingToken) {
            await hydrateAuthenticatedUser(existingToken)
            return
          }

          clearAuthState()

          if (config.auto_login && sortedUsers.length === 1) {
            const loginResponse = await localAuthAPI.login(
              config.default_user || undefined,
            )
            if (!active) return
            await applyLocalLogin(loginResponse)
          }
          return
        }

        if (authMode === 'hanko') {
          await registerHankoElements()
          const token = await getAuthToken()
          if (!active) return

          if (token) {
            const hankoUser = await getCurrentAuthUser()
            await hydrateAuthenticatedUser(
              token,
              hankoUser ? buildHankoFallbackUser(hankoUser) : undefined,
            )
          } else {
            clearAuthState()
          }
          return
        }

        clearAuthState()
      } catch (error) {
        if (!active) return
        console.error('Failed to initialize auth:', error)
        setAuthError(
          error instanceof Error ? error.message : 'Failed to initialize authentication',
        )
        clearAuthState()
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    void init()

    if (authMode === 'hanko') {
      void registerHankoElements().catch((error) => {
        console.error('Failed to register Hanko elements:', error)
      })
      const unsubscribeCreated = onSessionCreated(async () => {
        if (!active) return
        const token = await getAuthToken()
        if (token) {
          const hankoUser = await getCurrentAuthUser()
          await hydrateAuthenticatedUser(
            token,
            hankoUser ? buildHankoFallbackUser(hankoUser) : undefined,
          )
        } else {
          clearAuthState()
        }
      })
      const unsubscribeExpired = onSessionExpired(() => {
        if (active) {
          clearAuthState()
        }
      })

      return () => {
        active = false
        unsubscribeCreated()
        unsubscribeExpired()
      }
    }

    return () => {
      active = false
    }
  }, [applyLocalLogin, authMode, clearAuthState, hydrateAuthenticatedUser])

  const login = useCallback(
    async (email?: string) => {
      setAuthError(null)

      if (authMode === 'local') {
        setLoading(true)
        try {
          const response = await localAuthAPI.login(email || defaultLocalUser || undefined)
          await applyLocalLogin(response)
        } catch (error) {
          clearAuthState()
          const message =
            error instanceof Error ? error.message : 'Local sign-in failed'
          setAuthError(message)
          throw error
        } finally {
          setLoading(false)
        }
        return
      }

      if (authMode === 'hanko') {
        // Hanko login is handled by the <hanko-auth> web component directly.
        return
      }
    },
    [applyLocalLogin, authMode, clearAuthState, defaultLocalUser],
  )

  const register = useCallback(async () => {
    return
  }, [])

  const refreshIdToken = useCallback(async () => {
    if (authMode === 'local') {
      return localStorage.getItem('auth_token')
    }

    if (authMode === 'hanko') {
      const jwt = await getAuthToken()
      if (jwt) {
        localStorage.setItem('auth_token', jwt)
        setIdToken(jwt)
      }
      return jwt
    }

    return null
  }, [authMode])

  const refreshProfile = useCallback(async () => {
    const currentUser = await userAPI.getCurrentUser()
    const resolvedUser = currentUser.user
      ? {
          id: currentUser.user.id,
          email: currentUser.user.email,
          displayName:
            currentUser.user.displayName || currentUser.user.email,
          photoUrl: currentUser.user.photoUrl || null,
          username: currentUser.user.username || null,
          firstName: currentUser.user.firstName || null,
          lastName: currentUser.user.lastName || null,
          language: currentUser.user.language || null,
        }
      : getStoredAuthUserFallback() || null

    setUser(resolvedUser)
    setUserProfile(currentUser.user)
    const scopedOrganizations = filterScopedMemberships(currentUser.organizations)
    setOrganizations(scopedOrganizations)
    setHasOrganization(scopedOrganizations.length > 0)
    setNeedsEnrollment(false)
    setAuthError(null)
  }, [])

  const logout = useCallback(async () => {
    clearAuthState()
    setAuthError(null)
    if (authMode === 'local') {
      return
    }
    if (authMode === 'hanko') {
      await hankoSignOut()
      return
    }
  }, [authMode, clearAuthState])

  const completeEnrollment = useCallback(
    async (input: UserEnrollmentInput) => {
      const token = localStorage.getItem('auth_token') || idToken
      if (!token) {
        throw new Error('Authentication token is missing')
      }

      setLoading(true)
      setAuthError(null)
      try {
        resetScopedClientState(user?.id)
        try {
          await userAPI.enroll(input)
        } catch (error) {
          if (!isEnrollmentConflict(error)) {
            throw error
          }
        }
        const bootstrap = await userAPI.bootstrap()
        setRequiresOrganization(Boolean(bootstrap?.requires_organization))
        setHasOrganization(Boolean(bootstrap?.has_organization))
        await refreshProfile()
        setNeedsEnrollment(false)
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Enrollment failed'
        setAuthError(message)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [idToken, refreshProfile, resetScopedClientState, user?.id],
  )

  const value = useMemo(
    () => ({
      authMode,
      isAuthenticated: Boolean(user && idToken),
      loading,
      authError,
      user,
      userProfile,
      organizations,
      needsEnrollment,
      requiresOrganization,
      hasOrganization,
      localUsers,
      defaultLocalUser,
      idToken,
      login,
      register,
      logout,
      refreshIdToken,
      refreshProfile,
      completeEnrollment,
    }),
    [
      authError,
      authMode,
      completeEnrollment,
      defaultLocalUser,
      idToken,
      loading,
      login,
      localUsers,
      logout,
      needsEnrollment,
      organizations,
      refreshIdToken,
      refreshProfile,
      requiresOrganization,
      hasOrganization,
      register,
      user,
      userProfile,
    ],
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
